import { Box, CircularProgress, Grid2 } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCharleeAccolades } from '../api/api';
import { getUsecaseHref } from '../helper/hubItemHref';
import { HubPagination } from '../layout/HubPagination';
import { filterHubItems } from '../layout/filterHubItems';
import { useHubSearchQuery } from '../layout/useHubSearchQuery';
import { usePaginatedItems } from '../layout/usePaginatedItems';
import { KC_COLORS } from '../../knowledge-center/theme';
import { truncateAtWord } from '../../knowledge-center/truncateAtWord';
import '../../knowledge-center/knowledgeCenter.css';

const DESCRIPTION_CLAMP_CHARS = 155;
const textClampStyle = {
  overflowWrap: 'normal',
  wordBreak: 'normal',
  hyphens: 'none',
};

const USECASE_PAGE_LIMIT = 9;

function parseUsecases(accolades) {
  return accolades.map((item) => ({
    id: item.id,
    relevance: item.Relevance,
    title: item.title,
    description: item.Description,
    product: item.Product,
    area: item.Area,
  }));
}

function UsecaseCard({ usecase }) {
  const href = getUsecaseHref(usecase.relevance);

  const content = (
    <>
      <span className="kc-guides-list-card-accent" aria-hidden />
      <p
        className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.08em]"
        style={{ color: KC_COLORS.teal }}
      >
        Use case
      </p>
      <h3
        className="m-0 mb-2 h-[78px] line-clamp-3 text-[18px] font-semibold leading-[26px] transition-colors duration-200 group-hover:text-[#007a86]"
        style={{
          color: KC_COLORS.primaryNavy,
          ...textClampStyle,
        }}
        title={usecase.title}
      >
        {usecase.title}
      </h3>
      <p
        className="m-0 mb-0 h-[66px] line-clamp-3 text-[14px] leading-[22px]"
        style={{ color: KC_COLORS.gray600, ...textClampStyle }}
        title={usecase.description || undefined}
      >
        {usecase.description
          ? truncateAtWord(usecase.description, DESCRIPTION_CLAMP_CHARS)
          : null}
      </p>
      {(usecase.product || usecase.area) && (
        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          {usecase.product ? (
            <span
              className="rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em]"
              style={{ backgroundColor: KC_COLORS.tealLight, color: KC_COLORS.teal }}
            >
              {usecase.product}
            </span>
          ) : null}
          {usecase.area ? (
            <span
              className="rounded-md px-2.5 py-1 text-[11px] font-semibold"
              style={{ backgroundColor: KC_COLORS.lightGrey, color: KC_COLORS.navy700 }}
            >
              {usecase.area}
            </span>
          ) : null}
        </div>
      )}
      <span
        className={`${usecase.product || usecase.area ? 'mt-5' : 'mt-auto pt-4'} inline-flex items-center gap-1 text-[14px] font-semibold transition-all duration-200 group-hover:gap-2`}
        style={{ color: KC_COLORS.teal }}
      >
        View use case
        <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
      </span>
    </>
  );

  const cardClassName =
    'kc-guides-list-card kc-featured-card group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-4 text-left no-underline transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(10,29,58,0.12)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:p-[1.05rem]';

  if (!href) {
    return (
      <div className={cardClassName} aria-disabled>
        {content}
      </div>
    );
  }

  return (
    <Link
      to={href}
      className={cardClassName}
      style={{ color: 'inherit', outlineColor: KC_COLORS.teal }}
    >
      {content}
    </Link>
  );
}

export function UsecaseCardList() {
  const query = useHubSearchQuery();
  const hasQuery = Boolean(query);
  const [usecasesList, setUsecasesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { accolades } = await getCharleeAccolades(1, 200);
        setUsecasesList(parseUsecases(accolades));
      } catch {
        setUsecasesList([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(
    () => filterHubItems(usecasesList, query),
    [usecasesList, query]
  );
  const pagination = usePaginatedItems(filtered, USECASE_PAGE_LIMIT);
  const visibleUsecases = pagination.pageItems;
  const { totalPages, currentPage, onPageChange } = pagination;
  const matchCount = filtered.length;

  return (
    <Box className="kc-ds hub-listing w-full text-start">
      <h1 className="kc-type-h2 my-4" style={{ color: KC_COLORS.primaryNavy }}>
        Use cases
      </h1>

      {hasQuery ? (
        <p
          className="m-0 mb-4 text-[16px] leading-[24px]"
          style={{ color: KC_COLORS.gray600 }}
        >
          {matchCount === 0 ? (
            <>
              No results for{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                &apos;{query}&apos;
              </span>
            </>
          ) : (
            <>
              Showing{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                {matchCount}
              </span>{' '}
              {matchCount === 1 ? 'result' : 'results'} for{' '}
              <span className="font-semibold" style={{ color: KC_COLORS.primaryNavy }}>
                &apos;{query}&apos;
              </span>
            </>
          )}
        </p>
      ) : null}

      {loading ? (
        <Box className="flex min-h-[280px] w-full items-center justify-center py-8">
          <CircularProgress sx={{ color: KC_COLORS.teal }} />
        </Box>
      ) : visibleUsecases.length === 0 ? (
        <p className="py-12 text-center" style={{ color: KC_COLORS.gray600 }}>
          {hasQuery
            ? 'Try a different keyword, or clear search to browse this section.'
            : 'No use cases available.'}
        </p>
      ) : (
        <Grid2 container spacing={2.5} className="py-4">
          {visibleUsecases.map((usecase) => (
            <Grid2 key={usecase.relevance ?? usecase.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <UsecaseCard usecase={usecase} />
            </Grid2>
          ))}
        </Grid2>
      )}

      {!loading && visibleUsecases.length > 0 ? (
        <Box
          className="w-full py-6 md:py-8"
          sx={{ borderTop: `1px solid ${KC_COLORS.border}` }}
        >
          <HubPagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
          />
        </Box>
      ) : null}
    </Box>
  );
}
